import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaCrown, FaUserShield, FaUser, FaEye } from 'react-icons/fa';
import { USER_ROLE } from '../constants';
import { formatRoleName } from '../utils/auth-utils';

interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface ModernDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isRoleDropdown?: boolean;
}

const ModernDropdown: React.FC<ModernDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  isRoleDropdown = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selectedOption = options.find(option => option.value === value);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case USER_ROLE.SUPER_ADMIN:
        return <FaCrown />;
      case USER_ROLE.ADMIN:
        return <FaUserShield />;
      case USER_ROLE.USER:
        return <FaUser />;
      case USER_ROLE.Viewer:
        return <FaEye />;
      default:
        return <FaEye />;
    }
  };

  const getRoleIconClass = (role: string) => {
    switch (role) {
      case USER_ROLE.SUPER_ADMIN:
        return 'super-admin';
      case USER_ROLE.ADMIN:
        return 'admin';
      case USER_ROLE.USER:
        return 'user';
      case USER_ROLE.Viewer:
        return 'viewer';
      default:
        return 'viewer';
    }
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    console.log('Toggle clicked, disabled:', disabled, 'current isOpen:', isOpen);
    if (!disabled) {
      setIsOpen(prev => {
        console.log('Setting isOpen from', prev, 'to', !prev);
        return !prev;
      });
    }
  };

  console.log('Render - isOpen:', isOpen, 'options.length:', options.length);

  return (
    <div className={`modern-dropdown ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span className="dropdown-value">
          {selectedOption ? (
            isRoleDropdown ? (
              <div className="role-dropdown-option">
                <div className={`role-option-icon ${getRoleIconClass(selectedOption.value)}`}>
                  {getRoleIcon(selectedOption.value)}
                </div>
                <span className="role-option-text">{selectedOption.label}</span>
              </div>
            ) : (
              selectedOption.label
            )
          ) : (
            <span style={{ color: 'var(--text-tertiary)' }}>{placeholder}</span>
          )}
        </span>
        <FaChevronDown className="dropdown-icon" />
      </button>
      
      {isOpen && (
        <div className="dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`dropdown-option ${option.value === value ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option.value)}
            >
              {isRoleDropdown ? (
                <div className="role-dropdown-option">
                  <div className={`role-option-icon ${getRoleIconClass(option.value)}`}>
                    {getRoleIcon(option.value)}
                  </div>
                  <span className="role-option-text">{option.label}</span>
                </div>
              ) : (
                option.label
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernDropdown; 